import { useState, useRef, useEffect } from "react";
import { X, Sparkles, Send, Bot, User as UserIcon, Loader2, History, Plus, Trash2 } from "lucide-react";
import { API, api } from "../lib/api";

const MODELS = [
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6", tag: "Recommended" },
  { id: "gpt-5.2", label: "GPT-5.2", tag: "OpenAI" },
  { id: "gemini-3-flash-preview", label: "Gemini 3 Flash", tag: "Google" },
];

const greeting = (subject) => subject
  ? `Hi! I'm **BeeAI**, your study helper. I see you're studying **${subject}** — ask me anything, or say "quiz me on this" for a quick self-test.`
  : "Hi! I'm **BeeAI**, your personal study assistant. Ask me to explain concepts, generate practice questions, help with homework, or plan your study session.";

export default function AIAssistant({ open, onClose, subject }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/ai/sessions");
      setSessions(data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (open) {
      if (messages.length === 0) setMessages([{ role: "assistant", content: greeting(subject) }]);
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startNewChat = () => {
    setMessages([{ role: "assistant", content: greeting(subject) }]);
    setSessionId(null);
    setShowHistory(false);
  };

  const loadSession = async (sid) => {
    setLoadingSession(true);
    try {
      const { data } = await api.get(`/ai/sessions/${sid}`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
      setSessionId(sid);
      setShowHistory(false);
    } finally {
      setLoadingSession(false);
    }
  };

  const deleteSession = async (e, sid) => {
    e.stopPropagation(); e.preventDefault();
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/ai/sessions/${sid}`);
    setSessions((s) => s.filter((x) => x.session_id !== sid));
    if (sid === sessionId) startNewChat();
  };

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    const query = input;
    setInput("");
    setStreaming(true);

    try {
      const token = localStorage.getItem("focusbee_token");
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: query, model, session_id: sessionId, subject }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.type === "session") setSessionId(evt.session_id);
            else if (evt.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + evt.content };
                return copy;
              });
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${evt.message}` };
                return copy;
              });
            } else if (evt.type === "done") {
              loadSessions();
            }
          } catch { /* skip malformed */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 animate-fade-up" onClick={onClose} data-testid="ai-backdrop" />
      <aside
        data-testid="ai-panel"
        className="fixed right-0 top-0 h-full w-full sm:w-[520px] bg-[#1A1D20] text-white z-50 flex animate-fade-up"
      >
        {/* Sessions sidebar */}
        {showHistory && (
          <div data-testid="ai-history-sidebar" className="w-[180px] border-r border-white/10 flex flex-col bg-black/30">
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-white/60">History</span>
              <button onClick={() => setShowHistory(false)} className="text-white/50 hover:text-white text-xs">Close</button>
            </div>
            <button
              onClick={startNewChat}
              data-testid="ai-new-chat"
              className="mx-3 mt-3 mb-2 flex items-center justify-center gap-1 py-2 bg-[color:var(--bee-primary)] text-[color:var(--bee-text)] text-xs font-bold"
            >
              <Plus size={12} /> New chat
            </button>
            <div className="flex-1 overflow-y-auto scrollbar-thin px-2 pb-3">
              {sessions.length === 0 && <div className="text-[11px] text-white/40 px-2 py-3 text-center">No past chats.</div>}
              {sessions.map((s) => (
                <div
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  data-testid={`ai-session-${s.session_id}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") loadSession(s.session_id); }}
                  className={`group relative w-full text-left p-2 mb-1 text-[11px] cursor-pointer hover:bg-white/10 ${sessionId === s.session_id ? "bg-white/10 border-l-2 border-[color:var(--bee-primary)]" : ""}`}
                >
                  <div className="line-clamp-2 pr-4">{s.title}</div>
                  <div className="text-[9px] text-white/40 mt-1">{s.count} msg{s.count !== 1 && "s"}</div>
                  <button
                    onClick={(e) => deleteSession(e, s.session_id)}
                    className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 text-white/50 hover:text-red-400"
                    aria-label="Delete"
                  ><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center flex-shrink-0">
                <Sparkles size={18} className="text-[color:var(--bee-text)]" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg font-bold truncate">BeeAI Study Assistant</div>
                <div className="text-xs text-white/50">Powered by top-tier LLMs</div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setShowHistory((v) => !v)}
                data-testid="ai-toggle-history"
                className={`p-2 hover:bg-white/10 ${showHistory ? "bg-white/10" : ""}`}
                aria-label="Chat history"
                title="Chat history"
              ><History size={18} /></button>
              <button onClick={startNewChat} data-testid="ai-new-chat-header" className="p-2 hover:bg-white/10" aria-label="New chat" title="New chat">
                <Plus size={18} />
              </button>
              <button onClick={onClose} data-testid="close-ai" className="p-2 hover:bg-white/10" aria-label="Close">
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-xs">
            <span className="text-white/50">Model:</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              data-testid="ai-model-select"
              className="bg-white/5 border border-white/10 px-2 py-1 text-white text-xs outline-none focus:border-[color:var(--bee-primary)] flex-1"
            >
              {MODELS.map((m) => (
                <option key={m.id} value={m.id} className="bg-[#1A1D20]">{m.label} — {m.tag}</option>
              ))}
            </select>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin" data-testid="ai-messages">
            {loadingSession ? (
              <div className="flex items-center justify-center h-full text-white/50"><Loader2 className="animate-spin" size={20} /></div>
            ) : messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-7 h-7 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center flex-shrink-0">
                    <Bot size={14} className="text-[color:var(--bee-text)]" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[color:var(--bee-primary)] text-[color:var(--bee-text)] font-medium"
                      : "bg-white/5 border border-white/10"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="animate-spin" size={14} /> : "")}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <UserIcon size={14} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask BeeAI anything..."
                data-testid="ai-input"
                disabled={streaming}
                className="flex-1 bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[color:var(--bee-primary)]"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="ai-send"
                className="bg-[color:var(--bee-primary)] text-[color:var(--bee-text)] px-4 disabled:opacity-40 hover:brightness-110"
              >
                {streaming ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </button>
            </div>
            <div className="text-[10px] text-white/40 mt-2">BeeAI can make mistakes. Verify important information.</div>
          </div>
        </div>
      </aside>
    </>
  );
}
